import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import AccountProfileSidebar from './AccountProfileSidebar'
import { resolveActiveNavId } from './accountNavItems'
import { cn } from '../../lib/utils'

const DESKTOP_MQ = '(min-width: 768px)'

const AccountResponsiveShell = ({
  title = 'My account',
  profile,
  referralActive = false,
  hasVod = false,
  onLogout,
  showMobileHeader = true,
  backPath,
  activeNavId,
  children,
  className,
  contentClassName
}) => {
  const history = useHistory()
  const location = useLocation()
  const sidebarRef = useRef(null)
  const [sidebarHeight, setSidebarHeight] = useState(null)

  const resolvedActiveId = useMemo(
    () => activeNavId || resolveActiveNavId(location.pathname),
    [activeNavId, location.pathname]
  )

  useEffect(() => {
    const sidebarEl = sidebarRef.current
    if (!sidebarEl || typeof ResizeObserver === 'undefined') return undefined

    const media = window.matchMedia(DESKTOP_MQ)
    let rafId = 0
    let lastHeight = null

    const applyHeight = next => {
      if (next === lastHeight) return
      lastHeight = next
      setSidebarHeight(next)
    }

    const syncHeight = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        if (!media.matches) {
          applyHeight(null)
          return
        }
        const next = Math.round(sidebarEl.offsetHeight)
        applyHeight(next > 0 ? next : null)
      })
    }

    syncHeight()
    const observer = new ResizeObserver(syncHeight)
    observer.observe(sidebarEl)

    if (media.addEventListener) {
      media.addEventListener('change', syncHeight)
    } else {
      media.addListener(syncHeight)
    }

    return () => {
      cancelAnimationFrame(rafId)
      observer.disconnect()
      if (media.removeEventListener) {
        media.removeEventListener('change', syncHeight)
      } else {
        media.removeListener(syncHeight)
      }
    }
  }, [profile, resolvedActiveId, referralActive, hasVod])

  const handleAction = action => {
    if (action === 'logout') onLogout?.()
  }

  const handleBack = () => {
    if (backPath) {
      history.push(backPath)
      return
    }
    if (location.pathname === '/my-account') {
      history.push('/class-schedule')
      return
    }
    history.push('/my-account')
  }

  const showDesktopBackHeader = Boolean(backPath)

  return (
    <div className={cn('w-full bg-background', className)}>
      {showMobileHeader ? (
        <div className="sticky top-0 z-10 flex h-12 items-center gap-0.5 border-b border-border bg-background pl-1 pr-3 md:hidden">
          <button
            type="button"
            className="account-cta-transition flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground hover:bg-primary/5"
            onClick={handleBack}
            aria-label="Back"
          >
            <ArrowLeft className="size-5" strokeWidth={1.75} />
          </button>
          <h1
            className="m-0 min-w-0 flex-1 truncate p-0 font-semibold text-foreground"
            style={{ fontSize: 16, lineHeight: '20px' }}
          >
            {title}
          </h1>
        </div>
      ) : null}

      {/* max-w-[1120px] matches NavBar/Dweb NavContainer — no inner frame padding */}
      <div className="mx-auto flex w-full max-w-[1120px] flex-nowrap items-start gap-6 md:py-6">
        <div
          ref={sidebarRef}
          className="account-shell-sidebar-col hidden w-[280px] max-w-[280px] shrink-0 grow-0 basis-[280px] md:block"
        >
          <AccountProfileSidebar
            profile={profile}
            activeId={resolvedActiveId}
            referralActive={referralActive}
            hasVod={hasVod}
            onAction={handleAction}
          />
        </div>

        <main
          data-account-shell-main="true"
          className={cn(
            'relative min-h-0 min-w-0 flex-1 bg-background md:rounded-xl md:border md:border-border md:shadow-sm',
            contentClassName
          )}
          style={
            sidebarHeight
              ? {
                  height: sidebarHeight,
                  maxHeight: sidebarHeight
                }
              : undefined
          }
        >
          <div
            key={location.pathname}
            className="account-page-transition flex h-full min-h-0 flex-1 flex-col"
          >
            {showDesktopBackHeader ? (
              <div className="hidden shrink-0 items-center gap-2 border-b border-border bg-background px-6 py-5 md:flex">
                <button
                  type="button"
                  className="account-cta-transition flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground hover:bg-primary/5"
                  onClick={handleBack}
                  aria-label="Back"
                >
                  <ArrowLeft className="size-5" strokeWidth={1.75} />
                </button>
                <h2 className="m-0 min-w-0 truncate text-2xl font-semibold text-foreground">
                  {title}
                </h2>
              </div>
            ) : null}
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default AccountResponsiveShell
